import { STAMMDATEN_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission, useUsesExternalErp } from '@Contexts/appContext';
import { getFrameLabel } from '@Constants/getFrameLabel';
import type { ApiStorageLocation, EnumStorageTransactionType } from '@Model';
import { StorageLocationStockSection } from '@Modules/StorageTransactions/Stock/StorageLocationStockSection';
import { useOpenStorageTransaction } from '@Modules/StorageTransactions/useOpenStorageTransaction';
import { EditFrameMode, EditFrameShell, type IEditFrame, useGuiBinder } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { StorageLocationMainSection } from './Sections';

// @ai-generated 2026-10-21T10:16:14+00:00 by matthias.gessinger@synqony.com using claude-sonnet-5-5

interface IProps {
    frame: IEditFrame<ApiStorageLocation>;
}

export function StorageLocationFrameBody({ frame }: IProps) {
    const storageLocation = frame.data;
    const hasPermission = useHasPermission();
    const usesExternalErp = useUsesExternalErp();
    const { openBooking } = useOpenStorageTransaction();

    const showStock =
        frame.mode === EditFrameMode.View &&
        hasPermission(Permission.AuftragsbearbeitungStorageTransactionsRead) &&
        !usesExternalErp;

    const book = useCallback(
        (type?: EnumStorageTransactionType, onBooked?: () => void) =>
            void openBooking({ storageLocationId: storageLocation.id, type, onBooked }),
        [openBooking, storageLocation.id],
    );

    const binder = useGuiBinder<ApiStorageLocation>({
        data: storageLocation,
        meta: frame.meta,
        canEdit: frame.mode !== EditFrameMode.View,
        requestUpdateState: frame.requestUpdateState,
    });

    return (
        <EditFrameShell
            frame={frame}
            headerProps={{
                label: getFrameLabel('Lagerort', frame.mode),
                resourceLabel: 'Lagerort',
                ...frame.events,
            }}
            bannerProps={{
                mode: frame.mode,
                loading: frame.pendingAction !== undefined,
                ...frame.events,
            }}
        >
            <StorageLocationMainSection
                binder={binder}
                baseApiObjectPath={`${STAMMDATEN_API}/storage_locations/${storageLocation.id}`}
                requestUpdateState={frame.requestUpdateState}
                shorthand={storageLocation.shorthand}
                label={storageLocation.label}
                businessState={storageLocation.business_state}
            />
            {showStock && (
                <StorageLocationStockSection
                    storageLocationId={storageLocation.id}
                    storageLocationName={storageLocation.display_string}
                    onBook={book}
                />
            )}
        </EditFrameShell>
    );
}
